import api from "../../../../lib/api";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";

/**
 * RowShipMenu — the per-order "Ship ▾" dropdown on the ready-orders
 * table. Uses Shadcn DropdownMenu so it PORTALS to document.body —
 * escaping the readyOrders `overflow-y-auto` container that was
 * previously clipping the popup and making it look like it was
 * "overlapping / not opening properly".
 *
 * Actions:
 *   • Ship now  · POST /dropship/orders/{id}/ship-with-verqo
 *   • Compare rates · defers to openRateCompare(order_id)
 *   • Duplicate · POST /dropship/orders/{id}/duplicate
 *   • Combine   · POST /dropship/orders/combine  { order_ids }
 *   • Add tag   · defers to RowTagDialog
 *   • Split     · defers to RowSplitDialog
 *   • QR return · POST /dropship/orders/{id}/qr-return
 */
export default function RowShipMenu({
  order, selected, setSelected,
  rowMenuOid, setRowMenuOid,
  rowActionBusy, setRowActionBusy,
  setRowTagOid, setRowTagText,
  setRowSplitOid, setRowSplitParts,
  weight, reload,
  openRateCompare,
}) {
  const o = order;
  const isOpen = rowMenuOid === o.id;

  const shipNow = async () => {
    setRowMenuOid(null); setRowActionBusy(true);
    try {
      const { data } = await api.post(`/dropship/orders/${o.id}/ship-with-verqo`, {
        weight_lb: Number(o.weight_lb || weight || 1),
      });
      if (data.ok || data.already_shipped) {
        toast.success(`Shipped · ${data.tracking || o.code}`);
        if (data.label_url) window.open(data.label_url, "_blank");
        reload();
      } else {
        toast.error("Label buy failed — see history");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ship failed");
    } finally { setRowActionBusy(false); }
  };

  const duplicate = async () => {
    setRowMenuOid(null); setRowActionBusy(true);
    try {
      const { data } = await api.post(`/dropship/orders/${o.id}/duplicate`);
      toast.success(`Duplicated · ${data.duplicate?.code}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    } finally { setRowActionBusy(false); }
  };

  const combineWithSelected = async () => {
    if (selected.size < 1 || (selected.size === 1 && selected.has(o.id))) {
      toast.error("Select at least one other order first"); return;
    }
    setRowMenuOid(null); setRowActionBusy(true);
    const ids = Array.from(new Set([o.id, ...Array.from(selected)]));
    try {
      const { data } = await api.post(`/dropship/orders/combine`, { order_ids: ids });
      toast.success(`Combined ${data.order_count || ids.length} orders`);
      setSelected(new Set());
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Combine failed");
    } finally { setRowActionBusy(false); }
  };

  const qrReturn = async () => {
    setRowMenuOid(null); setRowActionBusy(true);
    try {
      const { data } = await api.post(`/dropship/orders/${o.id}/qr-return`, {
        original_transaction_id: o.shippo_transaction_id || o.id,
        include_qr: true,
      });
      if (data.qr_code_url) {
        window.open(data.qr_code_url, "_blank");
        toast.success("QR return ready");
      } else { toast.error("No QR URL returned"); }
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "QR return failed");
    } finally { setRowActionBusy(false); }
  };

  return (
    <div className="text-right">
      <DropdownMenu
        open={isOpen}
        onOpenChange={(v) => setRowMenuOid(v ? o.id : null)}
      >
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`row-ship-menu-${o.code}`}
            disabled={rowActionBusy}
            className="inline-flex items-center gap-0.5 rounded-full h-7 px-2.5 text-[10.5px] mono uppercase tracking-[0.14em] border border-border bg-white hover:bg-secondary/70 disabled:opacity-50"
            title="Row actions"
          >
            Ship <ChevronRight className="w-3 h-3 rotate-90"/>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="w-56 z-50"
          data-testid={`row-menu-${o.code}`}
        >
          <DropdownMenuItem
            onClick={shipNow}
            data-testid={`row-ship-now-${o.code}`}
            className="flex-col items-start gap-0.5 py-2"
          >
            <span className="font-medium text-[12px]">Ship now</span>
            <span className="text-[10.5px] text-foreground/55">Single label · uses defaults</span>
          </DropdownMenuItem>
          {openRateCompare && (
            <DropdownMenuItem
              onClick={() => { setRowMenuOid(null); openRateCompare(o.id); }}
              data-testid={`row-rate-compare-${o.code}`}
              className="flex-col items-start gap-0.5 py-2"
            >
              <span className="font-medium text-[12px]">Compare rates</span>
              <span className="text-[10.5px] text-foreground/55">Quote all carriers before you buy</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={duplicate}
            data-testid={`row-duplicate-${o.code}`}
            className="text-[12px]"
          >
            Duplicate order
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={combineWithSelected}
            data-testid={`row-combine-${o.code}`}
            className="text-[12px]"
          >
            Combine with selected
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => { setRowMenuOid(null); setRowTagOid(o.id); setRowTagText(""); }}
            data-testid={`row-tag-${o.code}`}
            className="text-[12px]"
          >
            Add tag…
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => { setRowMenuOid(null); setRowSplitOid(o.id); setRowSplitParts(2); }}
            data-testid={`row-split-${o.code}`}
            className="text-[12px]"
          >
            Split shipment…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={qrReturn}
            data-testid={`row-qr-return-${o.code}`}
            className="text-[12px]"
          >
            QR return label
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
